import React, { useState, useEffect, useCallback, useRef } from 'react';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { apiFetch } from '../utils/api';
import SEO from '../components/SEO';
import { Link, useNavigate } from 'react-router-dom';

const SERVER = process.env.NODE_ENV === 'development' ? '' : (process.env.REACT_APP_API_URL || 'https://server.autopilot.co.zw');

// ── Types ─────────────────────────────────────────────────────────────────────

interface Field { key: string; name: string; type: 'text' | 'number' | 'image' | 'boolean' | 'url'; required?: boolean; }
interface Table { slug: string; name: string; fields: Field[]; createdAt: number; rowCount: number; }
interface Row   { id: string; createdAt?: number; [key: string]: any; }

// ── Styles ────────────────────────────────────────────────────────────────────

const S = {
  page:       { background: 'var(--bg-page)', minHeight: '100vh', fontFamily: "'DM Sans', system-ui, sans-serif" } as React.CSSProperties,
  card:       { background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '14px', padding: '20px', marginBottom: '12px' } as React.CSSProperties,
  label:      { display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', letterSpacing: '0.06em', textTransform: 'uppercase' as const, marginBottom: '6px' },
  input:      { width: '100%', padding: '9px 12px', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' as const },
  select:     { padding: '9px 10px', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none' },
  btnPrimary: { padding: '8px 18px', background: '#5B4FE8', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' } as React.CSSProperties,
  btnGhost:   { padding: '7px 14px', background: 'var(--bg-element)', color: 'var(--text-secondary)', border: '1px solid var(--card-border)', borderRadius: '8px', cursor: 'pointer', fontSize: '12px' } as React.CSSProperties,
  btnDanger:  { padding: '6px 12px', background: '#ef444415', color: '#ef4444', border: '1px solid #ef444430', borderRadius: '7px', cursor: 'pointer', fontSize: '12px' } as React.CSSProperties,
  pill:       { display: 'inline-block', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', background: 'var(--bg-element)', color: 'var(--text-muted)' } as React.CSSProperties,
};

const TYPE_ICONS: Record<string, string> = { text: 'Aa', number: '##', image: '🖼', boolean: '✓', url: '🔗' };

// ── Helpers ────────────────────────────────────────────────────────────────────

function rowsToCsv(fields: Field[], rows: Row[]): string {
  const header = fields.map(f => `"${f.name}"`).join(',');
  const lines  = rows.map(row => fields.map(f => `"${String(row[f.key] ?? '').replace(/"/g, '""')}"`).join(','));
  return [header, ...lines].join('\n');
}

function parseCsv(text: string): string[][] {
  return text.trim().split('\n').map(line => {
    const cols: string[] = []; let cur = '', inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if      (ch === '"' && !inQ)                    { inQ = true; }
      else if (ch === '"' && inQ && line[i+1] === '"') { cur += '"'; i++; }
      else if (ch === '"' && inQ)                     { inQ = false; }
      else if (ch === ',' && !inQ)                    { cols.push(cur); cur = ''; }
      else                                            { cur += ch; }
    }
    cols.push(cur); return cols;
  });
}

function downloadCsv(filename: string, content: string) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([content], { type: 'text/csv' }));
  a.download = filename; a.click();
  URL.revokeObjectURL(a.href);
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1800); }}
      style={{ ...S.btnGhost, fontSize: '11px', padding: '4px 10px', whiteSpace: 'nowrap' as const }}>
      {copied ? '✓ Copied' : 'Copy'}
    </button>
  );
}

// ── These MUST be outside Database() to avoid remount-on-keypress ─────────────

interface FieldRowProps {
  idx: number; fields: Array<{ name: string; type: string }>;
  onChange: (idx: number, key: 'name' | 'type', val: string) => void;
  onRemove: (idx: number) => void;
}
function FieldRow({ idx, fields, onChange, onRemove }: FieldRowProps) {
  const f = fields[idx];
  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
      <input style={{ ...S.input, flex: 1 }} placeholder="Field name" value={f.name}
        onChange={e => onChange(idx, 'name', e.target.value)} />
      <select style={S.select} value={f.type} onChange={e => onChange(idx, 'type', e.target.value)}>
        <option value="text">Text</option>
        <option value="number">Number</option>
        <option value="url">URL</option>
        <option value="image">Image URL</option>
        <option value="boolean">Yes/No</option>
      </select>
      {fields.length > 1 && (
        <button style={{ ...S.btnDanger, padding: '7px 10px' }} onClick={() => onRemove(idx)}>✕</button>
      )}
    </div>
  );
}

interface CellInputProps { field: Field; value: string; onChange: (v: string) => void; }
function CellInput({ field, value, onChange }: CellInputProps) {
  if (field.type === 'boolean') return (
    <select style={{ ...S.select, width: '100%' }} value={value} onChange={e => onChange(e.target.value)}>
      <option value="true">Yes</option><option value="false">No</option>
    </select>
  );
  return <input style={S.input} type={field.type === 'number' ? 'number' : 'text'} placeholder={field.name} value={value} onChange={e => onChange(e.target.value)} />;
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function Database() {
  const navigate = useNavigate();
  const [uid, setUid]                  = useState<string | null>(null);
  const [tables, setTables]            = useState<Table[]>([]);
  const [tableLimit, setTableLimit]    = useState(1);
  const [plan, setPlan]                = useState('free');
  const [loading, setLoading]          = useState(true);
  const [activeTable, setActiveTable]  = useState<Table | null>(null);
  const [rows, setRows]                = useState<Row[]>([]);
  const [rowsLoading, setRowsLoading]  = useState(false);

  // Create table
  const [showCreate, setShowCreate]    = useState(false);
  const [createStep, setCreateStep]    = useState<1 | 2>(1);
  const [newName, setNewName]          = useState('');
  const [newFields, setNewFields]      = useState([{ name: '', type: 'text' }]);
  const [creating, setCreating]        = useState(false);
  const [createError, setCreateError]  = useState('');
  const csvStepRef                     = useRef<HTMLInputElement>(null);

  // Row CRUD
  const [showAddRow, setShowAddRow]    = useState(false);
  const [rowForm, setRowForm]          = useState<Record<string, string>>({});
  const [savingRow, setSavingRow]      = useState(false);
  const [editRowId, setEditRowId]      = useState<string | null>(null);
  const [editForm, setEditForm]        = useState<Record<string, string>>({});

  // Import
  const [importing, setImporting]      = useState(false);
  const [importMsg, setImportMsg]      = useState('');
  const importRef                      = useRef<HTMLInputElement>(null);

  const endpointUrl = (slug: string) => `${SERVER}/api/data/${uid}/${slug}`;
  const slugPreview = (name: string) => name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 40);

  // ── Auth ───────────────────────────────────────────────────────────────────

  useEffect(() => { return onAuthStateChanged(auth, u => setUid(u?.uid || null)); }, []);

  // ── Load tables ────────────────────────────────────────────────────────────

  const loadTables = useCallback(async () => {
    if (!uid) return;
    setLoading(true);
    try {
      const res  = await apiFetch('/api/db/tables');
      const data = await res.json();
      if (data.success) {
        setTables(data.tables || []);
        setTableLimit(data.limit ?? 1);
        setPlan(data.plan ?? 'free');
      }
    } catch (_) {}
    setLoading(false);
  }, [uid]);

  useEffect(() => { if (uid) loadTables(); }, [uid, loadTables]);

  // ── Load rows ──────────────────────────────────────────────────────────────

  const loadRows = useCallback(async (table: Table) => {
    setActiveTable(table); setRowsLoading(true); setShowAddRow(false); setEditRowId(null);
    try {
      const res  = await apiFetch(`/api/db/tables/${table.slug}/rows`);
      const data = await res.json();
      if (data.success) setRows(data.rows || []);
    } catch (_) {}
    setRowsLoading(false);
  }, []);

  // ── Field editor callbacks (stable refs — no remount) ──────────────────────

  const handleFieldChange = useCallback((idx: number, key: 'name' | 'type', val: string) => {
    setNewFields(prev => { const u = [...prev]; u[idx] = { ...u[idx], [key]: val }; return u; });
  }, []);

  const handleFieldRemove = useCallback((idx: number) => {
    setNewFields(prev => prev.filter((_, i) => i !== idx));
  }, []);

  // ── Create table ───────────────────────────────────────────────────────────

  function cancelCreate() {
    setShowCreate(false); setCreateStep(1); setNewName(''); setNewFields([{ name: '', type: 'text' }]); setCreateError('');
  }

  function handleCsvFieldDetect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const text = ev.target?.result as string;
      const firstLine = text.trim().split('\n')[0];
      const headers = parseCsv(firstLine)[0].map(h => h.trim());
      if (headers.length > 0) {
        setNewFields(headers.map(h => ({ name: h, type: 'text' as const })));
      }
      if (csvStepRef.current) csvStepRef.current.value = '';
    };
    reader.readAsText(file);
  }

  async function createTable() {
    const fields = newFields.filter(f => f.name.trim());
    if (!newName.trim() || fields.length === 0) return;
    setCreating(true); setCreateError('');
    try {
      const res  = await apiFetch('/api/db/tables', { method: 'POST', body: JSON.stringify({ name: newName, fields }) });
      const data = await res.json();
      if (data.success) {
        setCreating(false); cancelCreate();
        loadTables();
      } else {
        setCreateError(data.error || 'Failed to create table');
        setCreating(false);
      }
    } catch (err: any) {
      setCreateError(err.message || 'Network error');
      setCreating(false);
    }
  }

  async function deleteTable(slug: string) {
    if (!window.confirm('Delete this table and all its rows?')) return;
    await apiFetch(`/api/db/tables/${slug}`, { method: 'DELETE' });
    if (activeTable?.slug === slug) { setActiveTable(null); setRows([]); }
    await loadTables();
  }

  async function addRow() {
    if (!activeTable) return;
    setSavingRow(true);
    try {
      const body: Record<string, any> = {};
      for (const f of activeTable.fields) {
        const v = rowForm[f.key] ?? '';
        body[f.key] = f.type === 'number' ? (parseFloat(v) || 0) : f.type === 'boolean' ? (v === 'true') : v;
      }
      const res  = await apiFetch(`/api/db/tables/${activeTable.slug}/rows`, { method: 'POST', body: JSON.stringify(body) });
      const data = await res.json();
      if (data.success) {
        setSavingRow(false);
        setShowAddRow(false);
        setRowForm({});
        loadRows(activeTable);
        loadTables();
        return;
      }
    } catch (_) {}
    setSavingRow(false);
  }

  async function saveEdit(rowId: string) {
    if (!activeTable) return;
    const body: Record<string, any> = {};
    for (const f of activeTable.fields) {
      const v = editForm[f.key] ?? '';
      body[f.key] = f.type === 'number' ? (parseFloat(v) || 0) : f.type === 'boolean' ? (v === 'true') : v;
    }
    await apiFetch(`/api/db/tables/${activeTable.slug}/rows/${rowId}`, { method: 'PUT', body: JSON.stringify(body) });
    setEditRowId(null); await loadRows(activeTable);
  }

  async function deleteRow(rowId: string) {
    if (!activeTable) return;
    await apiFetch(`/api/db/tables/${activeTable.slug}/rows/${rowId}`, { method: 'DELETE' });
    await loadRows(activeTable); await loadTables();
  }

  function exportCsv() {
    if (!activeTable || rows.length === 0) return;
    downloadCsv(`${activeTable.slug}.csv`, rowsToCsv(activeTable.fields, rows));
  }

  function handleImportFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !activeTable) return;
    setImporting(true); setImportMsg('');
    const reader = new FileReader();
    reader.onload = async ev => {
      try {
        const text  = ev.target?.result as string;
        const lines = parseCsv(text);
        if (lines.length < 2) { setImportMsg('CSV needs a header row + at least one data row.'); setImporting(false); return; }
        const header   = lines[0].map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
        const dataRows = lines.slice(1)
          .map(cols => { const row: Record<string, any> = {}; header.forEach((h, i) => { row[h] = cols[i] ?? ''; }); return row; })
          .filter(r => Object.values(r).some(v => v !== ''));
        const res  = await apiFetch(`/api/db/tables/${activeTable!.slug}/import`, { method: 'POST', body: JSON.stringify({ rows: dataRows }) });
        const data = await res.json();
        if (data.success) {
          setImportMsg(`✅ Imported ${data.inserted} row${data.inserted !== 1 ? 's' : ''}${data.skipped > 0 ? ` (${data.skipped} skipped — table full)` : ''}.`);
          await loadRows(activeTable!); await loadTables();
        } else { setImportMsg(`❌ ${data.error}`); }
      } catch (err: any) { setImportMsg(`❌ ${err.message}`); }
      setImporting(false);
      if (importRef.current) importRef.current.value = '';
    };
    reader.readAsText(file);
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  const atLimit = tables.length >= tableLimit;

  return (
    <div style={S.page}>
      <SEO title="Database — Autopilot" description="Manage your data tables and API endpoints." noindex />
      <div style={{ maxWidth: '920px', margin: '0 auto', padding: '40px 24px' }}>

        <button onClick={() => navigate('/dashboard')} style={{ ...S.btnGhost, marginBottom: '16px' }}>← Dashboard</button>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px' }}>
          <div>
            <div style={{ fontSize: '22px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '5px' }}>🗄️ Database</div>
            <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
              Create tables and use the endpoint in any bot's flow API call.{' '}
              <span style={{ fontWeight: '600', color: atLimit ? '#ef4444' : 'var(--text-secondary)' }}>
                {tables.length}/{tableLimit} tables used
              </span>
              {atLimit && (
                <> · <Link to="/billing" style={{ color: '#5B4FE8', textDecoration: 'none', fontWeight: '600' }}>Upgrade to add more ↗</Link></>
              )}
            </div>
          </div>
          {!showCreate && !atLimit && (
            <button style={S.btnPrimary} onClick={() => { setShowCreate(true); setActiveTable(null); }}>+ New table</button>
          )}
          {!showCreate && atLimit && (
            <Link to="/billing" style={{ ...S.btnPrimary, textDecoration: 'none', background: 'linear-gradient(135deg,#5B4FE8,#7c3aed)' }}>
              Upgrade plan
            </Link>
          )}
        </div>

        {/* Plan limit banner */}
        {atLimit && (
          <div style={{ background: '#5B4FE810', border: '1px solid #5B4FE830', borderRadius: '12px', padding: '14px 18px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>Table limit reached on {plan} plan</div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>Free: 1 · Starter: 3 · Business: 5 · Agency: 7</div>
            </div>
            <Link to="/billing" style={{ ...S.btnPrimary, textDecoration: 'none', fontSize: '12px', padding: '6px 14px' }}>Upgrade</Link>
          </div>
        )}

        {/* Create table — Step 1: name */}
        {showCreate && createStep === 1 && (
          <div style={S.card}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '4px' }}>New table</div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '16px' }}>Step 1 of 2 — Name your table</div>
            <div style={{ marginBottom: '16px' }}>
              <label style={S.label}>Table name</label>
              <input style={S.input} placeholder="e.g. Products, Property Listings, Menu" value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && newName.trim() && setCreateStep(2)}
                autoFocus />
              {newName.trim() && uid && (
                <div style={{ marginTop: '6px', fontSize: '11px', color: 'var(--text-faint)' }}>
                  Endpoint: <code style={{ color: 'var(--text-muted)' }}>{SERVER}/api/data/{uid}/{slugPreview(newName)}</code>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button style={S.btnPrimary} onClick={() => newName.trim() && setCreateStep(2)} disabled={!newName.trim()}>Next →</button>
              <button style={S.btnGhost} onClick={cancelCreate}>Cancel</button>
            </div>
          </div>
        )}

        {/* Create table — Step 2: fields */}
        {showCreate && createStep === 2 && (
          <div style={S.card}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '4px' }}>
              {newName}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '16px' }}>Step 2 of 2 — Define fields or import from CSV</div>

            {/* CSV import to auto-detect fields */}
            <input ref={csvStepRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleCsvFieldDetect} />
            <button style={{ ...S.btnGhost, marginBottom: '16px', width: '100%', textAlign: 'center', padding: '10px', borderStyle: 'dashed' }}
              onClick={() => csvStepRef.current?.click()}>
              ⬆ Import CSV — auto-detect fields from headers
            </button>

            <label style={S.label}>Fields</label>
            {newFields.map((_, i) => (
              <FieldRow key={i} idx={i} fields={newFields} onChange={handleFieldChange} onRemove={handleFieldRemove} />
            ))}
            {newFields.length < 20 && (
              <button style={{ ...S.btnGhost, marginTop: '4px' }}
                onClick={() => setNewFields(prev => [...prev, { name: '', type: 'text' }])}>+ Add field</button>
            )}

            {createError && <div style={{ fontSize: '12px', color: '#ef4444', margin: '10px 0' }}>{createError}</div>}
            <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
              <button style={S.btnPrimary} onClick={createTable} disabled={creating || newFields.filter(f => f.name.trim()).length === 0}>
                {creating ? 'Creating...' : 'Create table'}
              </button>
              <button style={S.btnGhost} onClick={() => setCreateStep(1)}>← Back</button>
              <button style={{ ...S.btnGhost, marginLeft: 'auto' }} onClick={cancelCreate}>Cancel</button>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!loading && tables.length === 0 && !showCreate && (
          <div style={{ ...S.card, textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>🗄️</div>
            <div style={{ fontSize: '15px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '6px' }}>No tables yet</div>
            <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '20px' }}>
              Create a table for products, property listings, menus, or anything structured — then paste the endpoint into your bot's API call.
            </div>
            <button style={S.btnPrimary} onClick={() => setShowCreate(true)}>Create your first table</button>
          </div>
        )}

        {loading && <div style={{ color: 'var(--text-faint)', fontSize: '13px', padding: '20px 0' }}>Loading...</div>}

        {/* Tables */}
        {tables.map(t => (
          <div key={t.slug} style={{
            ...S.card, cursor: 'pointer',
            border: activeTable?.slug === t.slug ? '1px solid #5B4FE8' : '1px solid var(--card-border)',
          }} onClick={() => loadRows(t)}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)' }}>{t.name}</span>
                  <span style={S.pill}>{t.rowCount}/100 rows</span>
                </div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' as const, marginBottom: '10px' }}>
                  {(t.fields || []).map(f => (
                    <span key={f.key} style={{ ...S.pill, background: '#5B4FE815', color: '#818cf8' }}>
                      {TYPE_ICONS[f.type]} {f.name}
                    </span>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' as const }}>
                  <code style={{ fontSize: '11px', color: 'var(--text-muted)', background: 'var(--bg-element)', padding: '3px 8px', borderRadius: '5px', wordBreak: 'break-all' as const }}>
                    {endpointUrl(t.slug)}
                  </code>
                  <CopyButton text={endpointUrl(t.slug)} />
                </div>
              </div>
              <button style={{ ...S.btnDanger, marginLeft: '14px', flexShrink: 0 }}
                onClick={e => { e.stopPropagation(); deleteTable(t.slug); }}>Delete</button>
            </div>
          </div>
        ))}

        {/* Row editor */}
        {activeTable && (
          <div style={{ marginTop: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap' as const, gap: '8px' }}>
              <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)' }}>
                {activeTable.name} <span style={{ ...S.pill, marginLeft: '6px' }}>{rows.length}/100 rows</span>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input ref={importRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleImportFile} />
                <button style={S.btnGhost} onClick={() => importRef.current?.click()} disabled={importing}>
                  {importing ? 'Importing...' : '⬆ Import CSV'}
                </button>
                {rows.length > 0 && <button style={S.btnGhost} onClick={exportCsv}>⬇ Export CSV</button>}
                {rows.length < 100 && !showAddRow && (
                  <button style={S.btnPrimary} onClick={() => { setShowAddRow(true); setRowForm({}); }}>+ Add row</button>
                )}
              </div>
            </div>

            {importMsg && (
              <div style={{ fontSize: '12px', color: importMsg.startsWith('✅') ? '#10b981' : '#ef4444', marginBottom: '12px', padding: '8px 12px', background: importMsg.startsWith('✅') ? '#10b98110' : '#ef444410', borderRadius: '8px' }}>
                {importMsg}
                {!importMsg.startsWith('✅') && (
                  <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '4px' }}>
                    CSV headers must match field names: {activeTable.fields.map(f => f.name).join(', ')}
                  </div>
                )}
              </div>
            )}

            {showAddRow && (
              <div style={{ ...S.card, background: '#5B4FE808', border: '1px solid #5B4FE830', marginBottom: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '12px' }}>New row</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px', marginBottom: '14px' }}>
                  {activeTable.fields.map(f => (
                    <div key={f.key}>
                      <label style={S.label}>{f.name} <span style={{ color: 'var(--text-faint)', fontSize: '10px' }}>({f.type})</span></label>
                      <CellInput field={f} value={rowForm[f.key] ?? ''}
                        onChange={v => setRowForm(p => ({ ...p, [f.key]: v }))} />
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button style={S.btnPrimary} onClick={addRow} disabled={savingRow}>{savingRow ? 'Saving...' : 'Add row'}</button>
                  <button style={S.btnGhost} onClick={() => { setShowAddRow(false); setRowForm({}); }}>Cancel</button>
                </div>
              </div>
            )}

            {rowsLoading ? (
              <div style={{ color: 'var(--text-faint)', fontSize: '13px', padding: '20px 0' }}>Loading rows...</div>
            ) : rows.length === 0 ? (
              <div style={{ ...S.card, textAlign: 'center', padding: '30px', color: 'var(--text-muted)', fontSize: '13px' }}>
                No rows yet — add one manually or import a CSV.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' as const }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontSize: '13px' }}>
                  <thead>
                    <tr>
                      {activeTable.fields.map(f => (
                        <th key={f.key} style={{ textAlign: 'left' as const, padding: '8px 12px', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', letterSpacing: '0.05em', textTransform: 'uppercase' as const, borderBottom: '1px solid var(--card-border)', whiteSpace: 'nowrap' as const }}>
                          {TYPE_ICONS[f.type]} {f.name}
                        </th>
                      ))}
                      <th style={{ width: '130px', borderBottom: '1px solid var(--card-border)' }} />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(row => (
                      <tr key={row.id} style={{ borderBottom: '1px solid var(--card-border)' }}>
                        {activeTable.fields.map(f => (
                          <td key={f.key} style={{ padding: '10px 12px', color: 'var(--text-primary)', verticalAlign: 'middle' as const }}>
                            {editRowId === row.id ? (
                              <CellInput field={f} value={editForm[f.key] ?? String(row[f.key] ?? '')}
                                onChange={v => setEditForm(p => ({ ...p, [f.key]: v }))} />
                            ) : (
                              <span style={{ display: 'block', maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
                                {f.type === 'boolean' ? (row[f.key] ? '✅ Yes' : '❌ No') : String(row[f.key] ?? '—')}
                              </span>
                            )}
                          </td>
                        ))}
                        <td style={{ padding: '8px 12px', textAlign: 'right' as const, whiteSpace: 'nowrap' as const }}>
                          {editRowId === row.id ? (
                            <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                              <button style={{ ...S.btnPrimary, padding: '5px 12px', fontSize: '12px' }} onClick={() => saveEdit(row.id)}>Save</button>
                              <button style={S.btnGhost} onClick={() => setEditRowId(null)}>Cancel</button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                              <button style={S.btnGhost} onClick={() => {
                                setEditRowId(row.id);
                                const form: Record<string, string> = {};
                                for (const f of activeTable.fields) form[f.key] = String(row[f.key] ?? '');
                                setEditForm(form);
                              }}>Edit</button>
                              <button style={S.btnDanger} onClick={() => deleteRow(row.id)}>✕</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
